import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Search, X, Globe, Eye, CheckCircle2, Upload, Download, AlertCircle, CheckCircle, FileSpreadsheet } from 'lucide-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import {
    getCountries,
    createCountry,
    updateCountry,
    deleteCountry,
    toggleCountryStatus,
    checkCountryNameDuplicate,
    clearCountryNameCheck,
    clearError,
    downloadCountrySampleExcel,
    checkCountryBulkFormat,
    bulkUploadCountries
} from '../../redux/masterSlice';
import ErrorPage from '../../components/ErrorPage';
import MasterLabel from '../../components/common/MasterLabel';
import CountryFlag from '../../components/common/CountryFlag';
import useCanManage from '../../hooks/useCanManage';
import { formatTime } from '../../utils/clock';

const Country = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    // Write-side controls hide without the manage key — the API refuses
    // them anyway (hooks/useCanManage.js).
    const canManage = useCanManage('masters.manage');
    const {
        countries,
        countryLoading,
        countryCreateLoading,
        countryUpdateLoading,
        countryDeleteLoading,
        countryToggleLoading,
        countryPagination,
        countryNameCheckLoading,
        countryNameCheckResult,
        countryDownloadLoading,
        countryBulkUploadLoading,
        error
    } = useSelector((state) => state.master);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingCountry, setViewingCountry] = useState(null);
    const [editingCountry, setEditingCountry] = useState(null);
    const [formData, setFormData] = useState({
        countryName: '',
        countryCode: '',
        currency: '',
        currencySymbol: '',
        currencyCode: '',
        zipMinLength: '',
        zipMaxLength: '',
        mobileMinLength: '',
        mobileMaxLength: '',
        altMinLength: '',
        altMaxLength: '',
        timeZones: []
    });
    const [timeZoneInput, setTimeZoneInput] = useState('');
    const [errors, setErrors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [activeTab, setActiveTab] = useState('active');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [debouncedNameCheck, setDebouncedNameCheck] = useState('');

    // Bulk upload state — mirrors the Profession bulk upload modal.
    // Flag images are NOT in the Excel template; admins upload them
    // manually per country after the bulk insert via the Edit modal.
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [bulkSelectedFile, setBulkSelectedFile] = useState(null);
    const [bulkFileError, setBulkFileError] = useState('');
    const [bulkFormatChecking, setBulkFormatChecking] = useState(false);
    const [bulkFormatValid, setBulkFormatValid] = useState(false);
    const [bulkUploadResult, setBulkUploadResult] = useState(null);
    const bulkFileInputRef = useRef(null);

    const MAX_SEARCH_LENGTH = 50;
    const MAX_NAME_LENGTH = 100;

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Debounce country name check
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedNameCheck(formData.countryName.trim());
        }, 500);
        return () => clearTimeout(timer);
    }, [formData.countryName]);

    // Check country name duplicate
    useEffect(() => {
        if (debouncedNameCheck && debouncedNameCheck.length > 0) {
            if (!editingCountry || (editingCountry && debouncedNameCheck !== editingCountry.countryName)) {
                dispatch(checkCountryNameDuplicate(debouncedNameCheck));
            } else {
                dispatch(clearCountryNameCheck());
            }
        } else {
            dispatch(clearCountryNameCheck());
        }
    }, [debouncedNameCheck, editingCountry, dispatch]);

    // Fetch countries when search/page/tab changes
    useEffect(() => {
        dispatch(getCountries({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab
        }));
    }, [dispatch, currentPage, itemsPerPage, debouncedSearchQuery, activeTab]);

    const filteredCountries = countries || [];

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const validateSearchInput = (value) => {
        const sanitized = value.replace(/[<>]/g, '');
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            return t('country.searchExceedMaxLength', { max: MAX_SEARCH_LENGTH });
        }
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('country.searchCannotContainOnlySpaces');
        }
        return '';
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > MAX_SEARCH_LENGTH ? sanitized.substring(0, MAX_SEARCH_LENGTH) : sanitized;

        // Always update searchQuery so the input stays in sync
        setSearchQuery(finalValue);

        // Validate input
        const err = validateSearchInput(finalValue);
        setSearchError(err);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === 'countryName' || name === 'currency') {
            processedValue = value.replace(/[<>]/g, '');
            if (processedValue.length > MAX_NAME_LENGTH) {
                processedValue = processedValue.substring(0, MAX_NAME_LENGTH);
            }
        } else if (name === 'countryCode') {
            // Allow only + and digits
            processedValue = value.replace(/[^+\d]/g, '');
            if (processedValue.length > 5) {
                processedValue = processedValue.substring(0, 5);
            }
        } else if (name === 'currencySymbol') {
            if (processedValue.length > 5) {
                processedValue = processedValue.substring(0, 5);
            }
        } else if (name === 'currencyCode') {
            processedValue = value.replace(/[^A-Za-z]/g, '').toUpperCase();
            if (processedValue.length > 3) {
                processedValue = processedValue.substring(0, 3);
            }
        } else if (['zipMinLength', 'zipMaxLength', 'mobileMinLength', 'mobileMaxLength', 'altMinLength', 'altMaxLength'].includes(name)) {
            // Allow only digits
            processedValue = value.replace(/\D/g, '');
            if (processedValue.length > 2) {
                processedValue = processedValue.substring(0, 2);
            }
        }

        setFormData(prev => ({ ...prev, [name]: processedValue }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.countryName.trim()) {
            newErrors.countryName = t('country.countryNameRequired');
        } else if (formData.countryName.trim().length < 2) {
            newErrors.countryName = t('country.countryNameMinLength');
        } else if (!/^[A-Za-zÀ-ÿ\s\-']+$/.test(formData.countryName.trim())) {
            newErrors.countryName = t('country.countryNameOnlyLetters');
        }

        if (formData.countryName.trim() && countryNameCheckResult?.isDuplicate) {
            newErrors.countryName = countryNameCheckResult?.message?.en || t('country.countryNameAlreadyExists');
        }

        if (!formData.countryCode.trim()) {
            newErrors.countryCode = t('country.countryCodeRequired');
        } else if (!/^\+\d{1,4}$/.test(formData.countryCode.trim())) {
            newErrors.countryCode = t('country.countryCodeFormat');
        }

        if (!formData.currency.trim()) {
            newErrors.currency = t('country.currencyRequired');
        } else if (formData.currency.trim().length < 2) {
            newErrors.currency = t('country.currencyMinLength');
        }

        if (!formData.currencySymbol.trim()) {
            newErrors.currencySymbol = t('country.currencySymbolRequired');
        }

        if (!formData.currencyCode.trim()) {
            newErrors.currencyCode = t('country.currencyCodeRequired');
        } else if (!/^[A-Z]{3}$/.test(formData.currencyCode.trim())) {
            newErrors.currencyCode = t('country.currencyCodeFormat');
        }

        // Validate length fields
        const lengthFields = [
            { min: 'zipMinLength', max: 'zipMaxLength', label: 'ZIP' },
            { min: 'mobileMinLength', max: 'mobileMaxLength', label: 'Mobile' },
            { min: 'altMinLength', max: 'altMaxLength', label: 'Alt contact' }
        ];

        lengthFields.forEach(({ min, max, label }) => {
            if (!formData[min]) {
                newErrors[min] = t('country.minLengthRequired', { label });
            } else if (parseInt(formData[min]) < 1) {
                newErrors[min] = t('country.minLengthAtLeast1', { label });
            }
            if (!formData[max]) {
                newErrors[max] = t('country.maxLengthRequired', { label });
            } else if (parseInt(formData[max]) < 1) {
                newErrors[max] = t('country.maxLengthAtLeast1', { label });
            }
            if (formData[min] && formData[max] && parseInt(formData[min]) > parseInt(formData[max])) {
                newErrors[max] = t('country.maxLengthMustBeGreaterThanMin', { label });
            }
        });

        // Time Zones: at least one required
        if (!formData.timeZones || formData.timeZones.length === 0) {
            newErrors.timeZones = t('country.timeZoneRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = () => {
        setFormData({
            countryName: '',
            countryCode: '',
            dialCode: '',
            currency: '',
            currencySymbol: '',
            currencyCode: '',
            zipMinLength: '',
            zipMaxLength: '',
            mobileMinLength: '',
            mobileMaxLength: '',
            altMinLength: '',
            altMaxLength: '',
            timeZones: []
        });
        setTimeZoneInput('');
        setErrors({});
        dispatch(clearCountryNameCheck());
    };

    const handleAdd = () => {
        setEditingCountry(null);
        resetForm();
        setShowModal(true);
    };

    // ==================== BULK UPLOAD HANDLERS ====================

    const handleDownloadCountrySample = async () => {
        try {
            await dispatch(downloadCountrySampleExcel()).unwrap();
        } catch (err) {
            toast.warn(err?.error || 'Failed to download country sample template');
        }
    };

    const handleOpenBulkUpload = () => {
        setBulkSelectedFile(null);
        setBulkUploadResult(null);
        setBulkFileError('');
        setBulkFormatValid(false);
        setBulkFormatChecking(false);
        setShowBulkUploadModal(true);
    };

    const handleCloseBulkUpload = () => {
        setShowBulkUploadModal(false);
        setBulkSelectedFile(null);
        setBulkUploadResult(null);
        setBulkFileError('');
        setBulkFormatValid(false);
        setBulkFormatChecking(false);
    };

    const handleBulkFileSelect = async (e) => {
        const file = e.target.files?.[0];
        setBulkFileError('');
        setBulkUploadResult(null);
        setBulkFormatValid(false);
        if (!file) { setBulkSelectedFile(null); return; }

        const allowedTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel'
        ];
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!allowedTypes.includes(file.type) && !['.xlsx', '.xls'].includes(ext)) {
            setBulkFileError('Please upload a valid Excel file (.xlsx or .xls)');
            setBulkSelectedFile(null);
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setBulkFileError('File size must not exceed 5MB');
            setBulkSelectedFile(null);
            return;
        }

        setBulkSelectedFile(file);
        setBulkFormatChecking(true);
        try {
            await dispatch(checkCountryBulkFormat(file)).unwrap();
            setBulkFormatValid(true);
        } catch (err) {
            setBulkFormatValid(false);
            setBulkFileError(err?.error || 'Invalid Excel format');
        } finally {
            setBulkFormatChecking(false);
        }
    };

    const handleBulkUploadSubmit = async () => {
        if (!bulkSelectedFile) { setBulkFileError('Please select a file to upload'); return; }
        if (!bulkFormatValid) { setBulkFileError('Please fix the file format errors first'); return; }
        try {
            const result = await dispatch(bulkUploadCountries(bulkSelectedFile)).unwrap();
            setBulkUploadResult(result);
            refreshList(1);
            setCurrentPage(1);
        } catch (err) {
            toast.warn(err?.error || 'Failed to bulk upload countries');
        }
    };

    const handleBulkDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleBulkDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (file && bulkFileInputRef.current) {
            const dt = new DataTransfer();
            dt.items.add(file);
            bulkFileInputRef.current.files = dt.files;
            handleBulkFileSelect({ target: { files: dt.files } });
        }
    };

    const handleEdit = (country) => {
        setEditingCountry(country);
        setFormData({
            countryName: country.countryName || '',
            countryCode: country.countryCode || '',
            currency: country.currency || '',
            currencySymbol: country.currencySymbol || '',
            currencyCode: country.currencyCode || '',
            zipMinLength: country.zipMinLength?.toString() || '',
            zipMaxLength: country.zipMaxLength?.toString() || '',
            mobileMinLength: country.mobileMinLength?.toString() || '',
            mobileMaxLength: country.mobileMaxLength?.toString() || '',
            altMinLength: country.altMinLength?.toString() || '',
            altMaxLength: country.altMaxLength?.toString() || '',
            timeZones: Array.isArray(country.timeZones) ? [...country.timeZones] : []
        });
        setTimeZoneInput('');
        setErrors({});
        dispatch(clearCountryNameCheck());
        setShowModal(true);
    };

    const refreshList = (page = currentPage) => {
        dispatch(getCountries({
            page,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab
        }));
    };

    const handleDelete = async (country) => {
        try {
            const result = await Swal.fire({
                title: t('country.deleteCountry'),
                text: t('country.confirmDelete', { name: country.countryName }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#4ABDBD',
                confirmButtonText: t('country.yesDeleteIt'),
                cancelButtonText: t('country.cancel')
            });

            if (result.isConfirmed) {
                await dispatch(deleteCountry(country.countryId)).unwrap();
                toast.success(t('country.countryDeletedSuccessfully'));
                refreshList();
            }
        } catch (err) {
            // Clear the slice-level error so the page doesn't flip to ErrorPage
            // on the next render — this is a per-action failure, not a page load failure.
            dispatch(clearError());
            if (err?.inUse || err?.status === 409) {
                await Swal.fire({
                    title: t('country.cannotDeleteTitle', 'Cannot delete country'),
                    text: err?.error || t('country.inUseText', 'This country is assigned to one or more doctors/patients. Reassign them before deleting.'),
                    icon: 'info',
                    confirmButtonColor: '#4ABDBD',
                    confirmButtonText: t('country.ok', 'OK')
                });
                return;
            }
            toast.warn(err?.error || t('country.failedToDeleteCountry'));
        }
    };

    const handleStatusToggle = async (country) => {
        const newStatus = country.status === 'active' ? 'inactive' : 'active';

        try {
            const result = await Swal.fire({
                title: t('country.changeStatus'),
                text: t('country.confirmStatusChange', { action: newStatus === 'active' ? t('country.activate') : t('country.deactivate'), name: country.countryName }),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('country.confirm'),
                cancelButtonText: t('country.cancel'),
            });

            if (!result.isConfirmed) return;

            await dispatch(toggleCountryStatus({ id: country.countryId, status: newStatus })).unwrap();
            toast.success(newStatus === 'active' ? t('country.countryActivatedSuccessfully') : t('country.countryDeactivatedSuccessfully'));
            refreshList();
        } catch (err) {
            toast.warn(err?.error || t('country.failedToUpdateCountryStatus'));
        }
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            const fd = new FormData();
            fd.append('countryName', formData.countryName.trim());
            fd.append('countryCode', formData.countryCode.trim());
            fd.append('currency', formData.currency.trim());
            fd.append('currencySymbol', formData.currencySymbol.trim());
            fd.append('currencyCode', formData.currencyCode.trim());
            fd.append('zipMinLength', formData.zipMinLength);
            fd.append('zipMaxLength', formData.zipMaxLength);
            fd.append('mobileMinLength', formData.mobileMinLength);
            fd.append('mobileMaxLength', formData.mobileMaxLength);
            fd.append('altMinLength', formData.altMinLength);
            fd.append('altMaxLength', formData.altMaxLength);
            fd.append('timeZones', JSON.stringify(formData.timeZones));

            if (editingCountry) {
                await dispatch(updateCountry({ id: editingCountry.countryId, formData: fd })).unwrap();
                toast.success(t('country.countryUpdatedSuccessfully'));
                refreshList();
            } else {
                await dispatch(createCountry(fd)).unwrap();
                toast.success(t('country.countryCreatedSuccessfully'));
                setCurrentPage(1);
                refreshList(1);
            }

            setShowModal(false);
            resetForm();
            setEditingCountry(null);
        } catch (err) {
            toast.warn(err?.error || (editingCountry ? t('country.failedToUpdateCountry') : t('country.failedToCreateCountry')));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
        setEditingCountry(null);
    };

    const handleView = (country) => {
        setViewingCountry(country);
        setShowViewModal(true);
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setViewingCountry(null);
    };

    // Handle error state — only blank the page when the initial fetch fails
    // (no data yet). Per-action failures (delete/update/create/toggle) shouldn't
    // hijack the whole page; they're surfaced via toast/Swal in their handlers.
    const hasNoCountries = !countries || countries.length === 0;
    if (error && hasNoCountries && !countryLoading) {
        if (error?.status >= 500 && error?.status <= 599) {
            return <ErrorPage error={error} pageName={t('country.countryManagement')} />;
        }
        if (error?.message === 'Network Error') {
            return <ErrorPage error={error} pageName={t('country.countryManagement')} />;
        }
        return <ErrorPage error={error} pageName={t('country.countryManagement')} />;
    }

    return (
        <div data-testid="country-master-page" className="w-full">
            <div data-testid="country-master-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                {/* Header */}
                <div data-testid="country-master-header" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-4">
                    <h1 data-testid="country-master-heading" className="text-2xl sm:text-3xl pb-2 font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">
                        {t('country.countryManagement')}
                    </h1>
                    <div className="flex items-center gap-2 flex-wrap">
                        {canManage && (
                        <button
                            data-testid="country-master-add-button"
                            onClick={handleAdd}
                            className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
                        >
                            <Plus data-testid="country-master-add-icon" size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                            {t('country.addCountry')}
                        </button>
                        )}
                        {canManage && (
                        <button
                            data-testid="country-master-bulk-upload-button"
                            onClick={handleOpenBulkUpload}
                            className="group flex items-center gap-2 px-4 py-2 border-2 border-[#3AA8A8] text-[#3AA8A8] rounded-xl hover:bg-[#3AA8A8] hover:text-white transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer"
                            title="Bulk Upload"
                        >
                            <Upload size={18} />
                            <span className="hidden sm:inline">Bulk Upload</span>
                        </button>
                        )}
                    </div>
                </div>

                {/* Tab Bar + Search Row */}
                <div data-testid="country-master-tab-search-row" className="mb-5 flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div data-testid="country-master-tab-bar" className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                        <button
                            data-testid="country-master-tab-active"
                            onClick={() => { setActiveTab('active'); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'active' ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                        >
                            {activeTab === 'active' && (
                                <motion.div
                                    layoutId="activeCountryStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span data-testid="country-master-tab-active-label" className="relative z-20">{t('country.active')}</span>
                        </button>
                        <button
                            data-testid="country-master-tab-inactive"
                            onClick={() => { setActiveTab('inactive'); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'inactive' ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                        >
                            {activeTab === 'inactive' && (
                                <motion.div
                                    layoutId="activeCountryStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span data-testid="country-master-tab-inactive-label" className="relative z-20">{t('country.inactive')}</span>
                        </button>
                    </div>
                    <div className="relative group w-full sm:w-1/3">
                        <Search data-testid="country-master-search-icon" className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={22} />
                        <input
                            data-testid="country-master-search-input"
                            type="text"
                            placeholder={t('country.searchPlaceholder')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={50}
                            className={`w-full pl-12 pr-4 py-2 bg-gray-50 border-2 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400 ${searchError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                        />
                        {searchError && <p data-testid="country-master-search-error" className="text-red-600 text-xs mt-1 ml-1">{searchError}</p>}
                    </div>
                </div>

                {/* Table */}
                {(!countryLoading && filteredCountries.length === 0) ? (
                    <div data-testid="country-master-empty-state" className="text-center py-16 sm:py-20">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6">
                            <Globe data-testid="country-master-empty-icon" size={48} className="text-gray-400" />
                        </div>
                        <h3 data-testid="country-master-empty-title" className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2">
                            {searchQuery ? t('country.noCountriesFound') : t('country.noCountriesYet')}
                        </h3>
                        <p data-testid="country-master-empty-message" className="text-gray-500 mb-6 max-w-md mx-auto">
                            {searchQuery ? t('country.tryAdjustingSearch') : t('country.createFirstCountry')}
                        </p>
                        {canManage && !searchQuery && activeTab === 'active' && (
                            <button
                                data-testid="country-master-empty-create-button"
                                onClick={handleAdd}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                            >
                                <Plus data-testid="country-master-empty-create-icon" size={20} />
                                {t('country.createFirstCountry')}
                            </button>
                        )}
                    </div>
                ) : countryLoading ? (
                    <div data-testid="country-master-loading-container" className="flex items-center justify-center py-16 sm:py-20">
                        <div className="flex flex-col items-center gap-4">
                            <div data-testid="country-master-loading-spinner" className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-12 h-12 animate-spin"></div>
                            <p data-testid="country-master-loading-text" className="text-gray-500 text-sm">{t('country.loadingCountries')}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Desktop Table */}
                        <div data-testid="country-master-desktop-table-container" className="hidden sm:block overflow-x-auto rounded-md border border-gray-200 mb-4">
                            <table data-testid="country-master-desktop-table" className="w-full border-collapse rounded-md overflow-hidden">
                                <thead>
                                    <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                        <th data-testid="country-master-header-sno" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('country.sNo')}</th>
                                        <th data-testid="country-master-header-flag" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('country.flag')}</th>
                                        <th data-testid="country-master-header-name" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('country.countryName')}</th>
                                        <th data-testid="country-master-header-code" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('country.code')}</th>
                                        <th data-testid="country-master-header-currency" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('country.currency')}</th>
                                        <th data-testid="country-master-header-status" className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('country.status')}</th>
                                        <th data-testid="country-master-header-actions" className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('country.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCountries.map((country, index) => {
                                        const serialNumber = (currentPage - 1) * itemsPerPage + index + 1;
                                        const isInactive = country.status === 'inactive';
                                        return (
                                            <tr data-testid={`country-master-row-${country.countryId}`} key={country.countryId} className={`border-b border-gray-100/50 transition-all duration-300 group ${isInactive ? 'bg-gray-100/50' : 'hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80'}`}>
                                                <td data-testid={`country-master-row-serial-${country.countryId}`} className={`p-3 text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>{serialNumber}</td>
                                                <td className="p-3">
                                                    <CountryFlag
                                                        countryName={country.countryName}
                                                        alt={country.countryName}
                                                        className={`w-8 h-8 object-cover rounded-sm ${isInactive ? 'opacity-60' : ''}`}
                                                    />
                                                </td>
                                                <td data-testid={`country-master-row-name-${country.countryId}`} className={`p-3 text-xs font-semibold ${isInactive ? 'text-gray-400' : 'text-gray-800'}`}>
                                                    <MasterLabel type="masterCountry" id={country.countryId} field="countryName" fallback={country.countryName} />
                                                </td>
                                                <td data-testid={`country-master-row-code-${country.countryId}`} className={`p-3 text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>{country.countryCode}</td>
                                                <td data-testid={`country-master-row-currency-${country.countryId}`} className={`p-3 text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>
                                                    {country.currencySymbol && country.currencyCode
                                                        ? `${country.currencySymbol} (${country.currencyCode})`
                                                        : country.currency || '-'}
                                                </td>
                                                <td className="p-3 text-center">
                                                    <div className="flex justify-center">
                                                        <div
                                                            data-testid={`country-master-row-status-toggle-${country.countryId}`}
                                                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${isInactive ? 'bg-gray-300' : 'bg-[#4ABDBD]'} ${countryToggleLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                            onClick={() => canManage && !countryToggleLoading && handleStatusToggle(country)}
                                                        >
                                                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${isInactive ? 'translate-x-1' : 'translate-x-[18px]'}`}></span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-3">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            data-testid={`country-master-row-view-button-${country.countryId}`}
                                                            onClick={() => handleView(country)}
                                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden"
                                                            title={t('country.viewDetails')}
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <Eye className="h-4 w-4 relative z-10" />
                                                        </button>
                                                        {canManage && (
                                                        <button
                                                            data-testid={`country-master-row-edit-button-${country.countryId}`}
                                                            onClick={() => handleEdit(country)}
                                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden"
                                                            title={t('country.edit')}
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <Edit2 className="h-4 w-4 relative z-10" />
                                                        </button>
                                                        )}
                                                        {canManage && (
                                                        <button
                                                            data-testid={`country-master-row-delete-button-${country.countryId}`}
                                                            onClick={() => handleDelete(country)}
                                                            disabled={countryDeleteLoading}
                                                            className="group/btn text-red-500 hover:text-white p-2 rounded-lg bg-gradient-to-r from-red-50 to-red-50 hover:from-red-500 hover:to-red-600 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                                                            title={t('country.delete')}
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <Trash2 className="h-4 w-4 relative z-10" />
                                                        </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card Layout */}
                        <div data-testid="country-master-mobile-cards-container" className="block sm:hidden space-y-4 mb-4">
                            {filteredCountries.map((country, index) => {
                                const serialNumber = (currentPage - 1) * itemsPerPage + index + 1;
                                const isInactive = country.status === 'inactive';
                                return (
                                    <div data-testid={`country-master-card-${country.countryId}`} key={country.countryId} className={`p-4 rounded-xl shadow-md transition-all duration-300 ${isInactive ? 'bg-gray-100/50' : 'bg-gray-50 hover:shadow-lg'}`}>
                                        <div className="flex justify-between items-center mb-3">
                                            <div className="flex items-center gap-2">
                                                <CountryFlag data-testid={`country-master-card-flag-${country.countryId}`} countryName={country.countryName} alt={country.countryName} className={`w-8 h-8 object-cover rounded-sm ${isInactive ? 'opacity-60' : ''}`} />
                                                <span data-testid={`country-master-card-name-${country.countryId}`} className={`text-sm font-bold ${isInactive ? 'text-gray-400' : 'text-gray-800'}`}>
                                                    <MasterLabel type="masterCountry" id={country.countryId} field="countryName" fallback={country.countryName} />
                                                </span>
                                            </div>
                                            <span data-testid={`country-master-card-serial-${country.countryId}`} className={`text-xs font-bold ${isInactive ? 'text-gray-400' : 'text-[#4ABDBD]'}`}>#{serialNumber}</span>
                                        </div>
                                        <div className="space-y-1 mb-3">
                                            <div data-testid={`country-master-card-code-${country.countryId}`} className={`text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <span className="font-bold">{t('country.code')}:</span> {country.countryCode}
                                            </div>
                                            {country.currency && (
                                                <div data-testid={`country-master-card-currency-${country.countryId}`} className={`text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>
                                                    <span className="font-bold">{t('country.currency')}:</span> {country.currencySymbol} ({country.currencyCode})
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                            <div
                                                data-testid={`country-master-card-status-toggle-${country.countryId}`}
                                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${isInactive ? 'bg-gray-300' : 'bg-[#4ABDBD]'} ${countryToggleLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                onClick={() => canManage && !countryToggleLoading && handleStatusToggle(country)}
                                            >
                                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${isInactive ? 'translate-x-1' : 'translate-x-[18px]'}`}></span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button data-testid={`country-master-card-view-button-${country.countryId}`} onClick={() => handleView(country)} className="text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-blue-50 hover:bg-[#4ABDBD] shadow-sm cursor-pointer transition-all duration-300">
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                {canManage && (
                                                <button data-testid={`country-master-card-edit-button-${country.countryId}`} onClick={() => handleEdit(country)} className="text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-blue-50 hover:bg-[#4ABDBD] shadow-sm cursor-pointer transition-all duration-300">
                                                    <Edit2 className="h-4 w-4" />
                                                </button>
                                                )}
                                                {canManage && (
                                                <button data-testid={`country-master-card-delete-button-${country.countryId}`} onClick={() => handleDelete(country)} disabled={countryDeleteLoading} className="text-red-500 hover:text-white p-2 rounded-lg bg-red-50 hover:bg-red-500 shadow-sm cursor-pointer transition-all duration-300 disabled:opacity-50">
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Pagination */}
                        {countryPagination.total > 0 && (
                            <Pagination
                                totalItems={countryPagination.total}
                                currentPage={countryPagination.page || currentPage}
                                itemsPerPage={countryPagination.limit || itemsPerPage}
                                onPageChange={handlePageChange}
                                custombutton={filteredCountries.length === 0 ? 'disable' : ''}
                            />
                        )}
                    </>
                )}

                {/* Create/Edit Modal */}
                {showModal && (
                    <div data-testid="country-master-form-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div data-testid="country-master-form-modal-content" className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
                            {/* Modal Header */}
                            <div data-testid="country-master-form-modal-header" className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                                <h2 data-testid="country-master-form-modal-title" className="text-xl font-bold text-gray-800">
                                    {editingCountry ? t('country.editCountry') : t('country.addNewCountry')}
                                </h2>
                                <button data-testid="country-master-form-modal-close-button" onClick={handleCloseModal} className="p-1 hover:bg-gray-100 rounded transition-colors">
                                    <X data-testid="country-master-form-modal-close-icon" size={24} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div data-testid="country-master-form-modal-body" className="p-6 space-y-5">
                                {/* Country Name */}
                                <div data-testid="country-master-name-field-container">
                                    <label data-testid="country-master-name-label" className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('country.countryName')} <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            data-testid="country-master-name-input"
                                            type="text"
                                            name="countryName"
                                            value={formData.countryName}
                                            onChange={handleInputChange}
                                            maxLength={MAX_NAME_LENGTH}
                                            className={`w-full px-4 py-2 pr-10 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.countryName || (countryNameCheckResult?.isDuplicate && formData.countryName.trim())
                                                ? 'border-red-300 bg-red-50'
                                                : countryNameCheckResult && !countryNameCheckResult.isDuplicate && formData.countryName.trim() && !countryNameCheckLoading
                                                    ? 'border-green-300'
                                                    : 'border-gray-300'
                                                }`}
                                            placeholder={t('country.placeholderCountryName')}
                                        />
                                        {formData.countryName.trim() && !countryNameCheckLoading && countryNameCheckResult && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                {countryNameCheckResult.isDuplicate ? (
                                                    <X data-testid="country-master-name-duplicate-icon" className="h-5 w-5 text-red-500" />
                                                ) : (
                                                    <CheckCircle2 data-testid="country-master-name-valid-icon" className="h-5 w-5 text-green-500" />
                                                )}
                                            </div>
                                        )}
                                        {countryNameCheckLoading && formData.countryName.trim() && (
                                            <div data-testid="country-master-name-checking-spinner" className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                <div className="border-2 border-[#4ABDBD] border-t-transparent rounded-full w-5 h-5 animate-spin"></div>
                                            </div>
                                        )}
                                    </div>
                                    {errors.countryName && <p data-testid="country-master-name-error" className="mt-1 text-sm text-red-500">{errors.countryName}</p>}
                                    {!errors.countryName && countryNameCheckResult?.isDuplicate && formData.countryName.trim() && (
                                        <p data-testid="country-master-name-duplicate-error" className="mt-1 text-sm text-red-500">{countryNameCheckResult?.message?.en || t('country.countryNameAlreadyExists')}</p>
                                    )}
                                </div>

                                {/* Country Code */}
                                <div data-testid="country-master-code-field-container">
                                    <label data-testid="country-master-code-label" className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('country.countryCodeDialCode')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        data-testid="country-master-code-input"
                                        type="text"
                                        name="countryCode"
                                        value={formData.countryCode}
                                        onChange={handleInputChange}
                                        maxLength={5}
                                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.countryCode ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                        placeholder={t('country.placeholderCountryCode')}
                                    />
                                    {errors.countryCode && <p data-testid="country-master-code-error" className="mt-1 text-sm text-red-500">{errors.countryCode}</p>}
                                </div>

                                {/* Currency Name */}
                                <div data-testid="country-master-currency-field-container">
                                    <label data-testid="country-master-currency-label" className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('country.currency')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        data-testid="country-master-currency-input"
                                        type="text"
                                        name="currency"
                                        value={formData.currency}
                                        onChange={handleInputChange}
                                        maxLength={MAX_NAME_LENGTH}
                                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.currency ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                        placeholder={t('country.placeholderCurrency')}
                                    />
                                    {errors.currency && <p data-testid="country-master-currency-error" className="mt-1 text-sm text-red-500">{errors.currency}</p>}
                                </div>

                                {/* Currency Symbol & Code */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div data-testid="country-master-currency-symbol-field-container">
                                        <label data-testid="country-master-currency-symbol-label" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('country.currencySymbol')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            data-testid="country-master-currency-symbol-input"
                                            type="text"
                                            name="currencySymbol"
                                            value={formData.currencySymbol}
                                            onChange={handleInputChange}
                                            maxLength={5}
                                            className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.currencySymbol ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                            placeholder={t('country.placeholderCurrencySymbol')}
                                        />
                                        {errors.currencySymbol && <p data-testid="country-master-currency-symbol-error" className="mt-1 text-sm text-red-500">{errors.currencySymbol}</p>}
                                    </div>
                                    <div data-testid="country-master-currency-code-field-container">
                                        <label data-testid="country-master-currency-code-label" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('country.currencyCode')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            data-testid="country-master-currency-code-input"
                                            type="text"
                                            name="currencyCode"
                                            value={formData.currencyCode}
                                            onChange={handleInputChange}
                                            maxLength={3}
                                            className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.currencyCode ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                            placeholder={t('country.placeholderCurrencyCode')}
                                        />
                                        {errors.currencyCode && <p data-testid="country-master-currency-code-error" className="mt-1 text-sm text-red-500">{errors.currencyCode}</p>}
                                    </div>
                                </div>

                                {/* ZIP Length */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div data-testid="country-master-zip-min-field-container">
                                        <label data-testid="country-master-zip-min-label" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('country.zipMinLength')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            data-testid="country-master-zip-min-input"
                                            type="text"
                                            name="zipMinLength"
                                            value={formData.zipMinLength}
                                            onChange={handleInputChange}
                                            maxLength={2}
                                            className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.zipMinLength ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                            placeholder={t('country.placeholderZipMin')}
                                        />
                                        {errors.zipMinLength && <p data-testid="country-master-zip-min-error" className="mt-1 text-sm text-red-500">{errors.zipMinLength}</p>}
                                    </div>
                                    <div data-testid="country-master-zip-max-field-container">
                                        <label data-testid="country-master-zip-max-label" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('country.zipMaxLength')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            data-testid="country-master-zip-max-input"
                                            type="text"
                                            name="zipMaxLength"
                                            value={formData.zipMaxLength}
                                            onChange={handleInputChange}
                                            maxLength={2}
                                            className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.zipMaxLength ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                            placeholder={t('country.placeholderZipMax')}
                                        />
                                        {errors.zipMaxLength && <p data-testid="country-master-zip-max-error" className="mt-1 text-sm text-red-500">{errors.zipMaxLength}</p>}
                                    </div>
                                </div>

                                {/* Mobile Length */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div data-testid="country-master-mobile-min-field-container">
                                        <label data-testid="country-master-mobile-min-label" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('country.mobileMinLength')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            data-testid="country-master-mobile-min-input"
                                            type="text"
                                            name="mobileMinLength"
                                            value={formData.mobileMinLength}
                                            onChange={handleInputChange}
                                            maxLength={2}
                                            className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.mobileMinLength ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                            placeholder={t('country.placeholderLength')}
                                        />
                                        {errors.mobileMinLength && <p data-testid="country-master-mobile-min-error" className="mt-1 text-sm text-red-500">{errors.mobileMinLength}</p>}
                                    </div>
                                    <div data-testid="country-master-mobile-max-field-container">
                                        <label data-testid="country-master-mobile-max-label" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('country.mobileMaxLength')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            data-testid="country-master-mobile-max-input"
                                            type="text"
                                            name="mobileMaxLength"
                                            value={formData.mobileMaxLength}
                                            onChange={handleInputChange}
                                            maxLength={2}
                                            className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.mobileMaxLength ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                            placeholder={t('country.placeholderLength')}
                                        />
                                        {errors.mobileMaxLength && <p data-testid="country-master-mobile-max-error" className="mt-1 text-sm text-red-500">{errors.mobileMaxLength}</p>}
                                    </div>
                                </div>

                                {/* Alt Contact Length */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div data-testid="country-master-alt-min-field-container">
                                        <label data-testid="country-master-alt-min-label" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('country.altContactMinLength')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            data-testid="country-master-alt-min-input"
                                            type="text"
                                            name="altMinLength"
                                            value={formData.altMinLength}
                                            onChange={handleInputChange}
                                            maxLength={2}
                                            className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.altMinLength ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                            placeholder={t('country.placeholderLength')}
                                        />
                                        {errors.altMinLength && <p data-testid="country-master-alt-min-error" className="mt-1 text-sm text-red-500">{errors.altMinLength}</p>}
                                    </div>
                                    <div data-testid="country-master-alt-max-field-container">
                                        <label data-testid="country-master-alt-max-label" className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('country.altContactMaxLength')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            data-testid="country-master-alt-max-input"
                                            type="text"
                                            name="altMaxLength"
                                            value={formData.altMaxLength}
                                            onChange={handleInputChange}
                                            maxLength={2}
                                            className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.altMaxLength ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                            placeholder={t('country.placeholderLength')}
                                        />
                                        {errors.altMaxLength && <p data-testid="country-master-alt-max-error" className="mt-1 text-sm text-red-500">{errors.altMaxLength}</p>}
                                    </div>
                                </div>

                                {/* Time Zones */}
                                <div data-testid="country-master-timezones-field-container">
                                    <label data-testid="country-master-timezones-label" className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('country.timeZones')} <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex gap-2 mb-2">
                                        <input
                                            data-testid="country-master-timezones-input"
                                            type="text"
                                            value={timeZoneInput}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/[<>]/g, '');
                                                setTimeZoneInput(val.substring(0, 50));
                                                if (errors.timeZones) setErrors(prev => ({ ...prev, timeZones: '' }));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    const trimmed = timeZoneInput.trim();
                                                    if (trimmed && !formData.timeZones.includes(trimmed)) {
                                                        setFormData(prev => ({ ...prev, timeZones: [...prev.timeZones, trimmed] }));
                                                        setTimeZoneInput('');
                                                        if (errors.timeZones) setErrors(prev => ({ ...prev, timeZones: '' }));
                                                    }
                                                }
                                            }}
                                            className={`flex-1 px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.timeZones ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                            placeholder={t('country.placeholderTimeZone')}
                                        />
                                        <button
                                            data-testid="country-master-timezones-add-button"
                                            type="button"
                                            onClick={() => {
                                                const trimmed = timeZoneInput.trim();
                                                if (trimmed && !formData.timeZones.includes(trimmed)) {
                                                    setFormData(prev => ({ ...prev, timeZones: [...prev.timeZones, trimmed] }));
                                                    setTimeZoneInput('');
                                                    if (errors.timeZones) setErrors(prev => ({ ...prev, timeZones: '' }));
                                                }
                                            }}
                                            className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-sm cursor-pointer"
                                        >
                                            <Plus data-testid="country-master-timezones-add-icon" size={18} />
                                        </button>
                                    </div>
                                    {errors.timeZones && <p data-testid="country-master-timezones-error" className="mt-1 text-sm text-red-500">{errors.timeZones}</p>}
                                    {formData.timeZones.length > 0 && (
                                        <div data-testid="country-master-timezones-list" className="flex flex-wrap gap-2 mt-2">
                                            {formData.timeZones.map((tz, idx) => (
                                                <span
                                                    data-testid={`country-master-timezone-tag-${idx}`}
                                                    key={idx}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-cyan-50 text-[#3AA8A8] border border-[#4ABDBD]/20 rounded-lg text-sm font-medium"
                                                >
                                                    {tz}
                                                    <button
                                                        data-testid={`country-master-timezone-remove-button-${idx}`}
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                timeZones: prev.timeZones.filter((_, i) => i !== idx)
                                                            }));
                                                        }}
                                                        className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <p data-testid="country-master-timezones-hint" className="text-xs text-gray-400 mt-1">{t('country.timeZoneHint')}</p>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div data-testid="country-master-form-modal-footer" className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                                <button
                                    data-testid="country-master-form-modal-cancel-button"
                                    onClick={handleCloseModal}
                                    disabled={countryCreateLoading || countryUpdateLoading}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {t('country.cancel')}
                                </button>
                                <button
                                    data-testid="country-master-form-modal-save-button"
                                    onClick={handleSave}
                                    disabled={countryCreateLoading || countryUpdateLoading}
                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {(countryCreateLoading || countryUpdateLoading) && (
                                        <span data-testid="country-master-form-modal-save-loading" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                    )}
                                    {countryCreateLoading || countryUpdateLoading
                                        ? (editingCountry ? t('country.updating') : t('country.creating'))
                                        : (editingCountry ? t('country.update') : t('country.create'))}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* View Country Modal */}
                {showViewModal && viewingCountry && (
                    <div data-testid="country-master-view-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div data-testid="country-master-view-modal-content" className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
                            {/* Modal Header */}
                            <div data-testid="country-master-view-modal-header" className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                                <h2 data-testid="country-master-view-modal-title" className="text-xl font-bold text-gray-800">{t('country.countryDetails')}</h2>
                                <button data-testid="country-master-view-modal-close-button" onClick={handleCloseViewModal} className="p-1 hover:bg-gray-100 rounded transition-colors">
                                    <X data-testid="country-master-view-modal-close-icon" size={24} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div data-testid="country-master-view-modal-body" className="p-6 space-y-4">
                                {/* Flag & Name */}
                                <div data-testid="country-master-view-flag-name-section" className="flex items-center gap-4 mb-4">
                                    <CountryFlag data-testid="country-master-view-flag" countryName={viewingCountry.countryName} alt={viewingCountry.countryName} className="w-16 h-16 object-cover rounded-sm" />
                                    <div>
                                        <h3 data-testid="country-master-view-name" className="text-lg font-bold text-gray-800">
                                            <MasterLabel type="masterCountry" id={viewingCountry.countryId} field="countryName" fallback={viewingCountry.countryName} />
                                        </h3>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label data-testid="country-master-view-code-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.countryCode')}</label>
                                        <div data-testid="country-master-view-code-value" className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800 font-semibold">{viewingCountry.countryCode}</div>
                                    </div>
                                    <div>
                                        <label data-testid="country-master-view-status-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.status')}</label>
                                        <div className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50">
                                            <span data-testid="country-master-view-status-value" className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${viewingCountry.status === 'active' ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-gray-200 text-gray-500 border border-gray-300'}`}>
                                                {viewingCountry.status === 'active' ? t('country.active') : t('country.inactive')}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label data-testid="country-master-view-currency-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.currency')}</label>
                                        <div data-testid="country-master-view-currency-value" className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800">{viewingCountry.currency || '-'}</div>
                                    </div>
                                    <div>
                                        <label data-testid="country-master-view-currency-symbol-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.currencySymbol')}</label>
                                        <div data-testid="country-master-view-currency-symbol-value" className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800 font-semibold">{viewingCountry.currencySymbol || '-'}</div>
                                    </div>
                                    <div>
                                        <label data-testid="country-master-view-currency-code-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.currencyCode')}</label>
                                        <div data-testid="country-master-view-currency-code-value" className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800">{viewingCountry.currencyCode || '-'}</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label data-testid="country-master-view-zip-min-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.zipMinLength')}</label>
                                        <div data-testid="country-master-view-zip-min-value" className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800">{viewingCountry.zipMinLength}</div>
                                    </div>
                                    <div>
                                        <label data-testid="country-master-view-zip-max-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.zipMaxLength')}</label>
                                        <div data-testid="country-master-view-zip-max-value" className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800">{viewingCountry.zipMaxLength}</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label data-testid="country-master-view-mobile-min-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.mobileMinLength')}</label>
                                        <div data-testid="country-master-view-mobile-min-value" className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800">{viewingCountry.mobileMinLength}</div>
                                    </div>
                                    <div>
                                        <label data-testid="country-master-view-mobile-max-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.mobileMaxLength')}</label>
                                        <div data-testid="country-master-view-mobile-max-value" className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800">{viewingCountry.mobileMaxLength}</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label data-testid="country-master-view-alt-min-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.altContactMinLength')}</label>
                                        <div data-testid="country-master-view-alt-min-value" className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800">{viewingCountry.altMinLength}</div>
                                    </div>
                                    <div>
                                        <label data-testid="country-master-view-alt-max-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.altContactMaxLength')}</label>
                                        <div data-testid="country-master-view-alt-max-value" className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800">{viewingCountry.altMaxLength}</div>
                                    </div>
                                </div>

                                {viewingCountry.timeZones && viewingCountry.timeZones.length > 0 && (
                                    <div data-testid="country-master-view-timezones-section">
                                        <label data-testid="country-master-view-timezones-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.timeZones')}</label>
                                        <div className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50">
                                            <div className="flex flex-wrap gap-2">
                                                {viewingCountry.timeZones.map((tz, idx) => (
                                                    <span
                                                        data-testid={`country-master-view-timezone-tag-${idx}`}
                                                        key={idx}
                                                        className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-blue-50 to-cyan-50 text-[#3AA8A8] border border-[#4ABDBD]/20 rounded-lg text-xs font-medium"
                                                    >
                                                        {tz}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {viewingCountry.createdAt && (
                                    <div data-testid="country-master-view-created-at-section">
                                        <label data-testid="country-master-view-created-at-label" className="block text-xs font-medium text-gray-500 mb-1">{t('country.createdAt')}</label>
                                        <div data-testid="country-master-view-created-at-value" className="px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800">
                                            {new Date(viewingCountry.createdAt).toLocaleDateString()}, {formatTime(viewingCountry.createdAt)}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div data-testid="country-master-view-modal-footer" className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
                                <button
                                    data-testid="country-master-view-modal-close-footer-button"
                                    onClick={handleCloseViewModal}
                                    className="px-6 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md"
                                >
                                    {t('country.close')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bulk Upload Modal */}
                {showBulkUploadModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="country-master-bulk-upload-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                    <Upload size={22} className="text-[#4ABDBD]" />
                                    Bulk Upload Countries
                                </h2>
                                <button onClick={handleCloseBulkUpload} className="p-1 hover:bg-gray-100 rounded transition-colors" data-testid="country-master-bulk-upload-modal-close-button">
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="p-6 space-y-6">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                                        <AlertCircle size={16} />
                                        Instructions
                                    </h3>
                                    <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                                        <li>Download the sample template — it's pre-filled with all 197 countries.</li>
                                        <li>Edit any cells you need (currency, mobile / zip lengths, time zones), then save and upload.</li>
                                        <li>Flag images are <strong>not</strong> in the Excel — upload each country's flag manually via the Edit screen after the bulk insert.</li>
                                        <li>Duplicate country names (case-insensitive among live rows) are skipped.</li>
                                        <li>Maximum file size: 5MB. Accepted formats: .xlsx, .xls.</li>
                                    </ul>
                                    <button
                                        onClick={handleDownloadCountrySample}
                                        disabled={countryDownloadLoading}
                                        className="mt-3 inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 font-semibold underline cursor-pointer disabled:opacity-50"
                                        data-testid="country-master-download-sample-button"
                                    >
                                        {countryDownloadLoading
                                            ? <span className="border-2 border-blue-700 border-t-transparent rounded-full w-3 h-3 animate-spin"></span>
                                            : <Download size={14} />}
                                        Download sample template
                                    </button>
                                </div>

                                {!bulkUploadResult && (
                                    <div
                                        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${bulkSelectedFile ? 'border-[#4ABDBD] bg-[#4ABDBD]/5' : bulkFileError ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] hover:bg-gray-50'}`}
                                        onDragOver={handleBulkDragOver}
                                        onDrop={handleBulkDrop}
                                        data-testid="country-master-file-dropzone"
                                    >
                                        <input
                                            ref={bulkFileInputRef}
                                            type="file"
                                            accept=".xlsx,.xls"
                                            onChange={handleBulkFileSelect}
                                            className="hidden"
                                            id="countryBulkUploadInput"
                                            data-testid="country-master-file-input"
                                        />
                                        {bulkSelectedFile ? (
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-16 h-16 bg-[#4ABDBD]/10 rounded-full flex items-center justify-center">
                                                    <FileSpreadsheet size={32} className="text-[#4ABDBD]" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800">{bulkSelectedFile.name}</p>
                                                    <p className="text-xs text-gray-500 mt-1">{(bulkSelectedFile.size / 1024).toFixed(1)} KB</p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setBulkSelectedFile(null);
                                                        setBulkFileError('');
                                                        setBulkFormatValid(false);
                                                        setBulkFormatChecking(false);
                                                        if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
                                                    }}
                                                    className="text-sm text-red-500 hover:text-red-700 font-medium flex items-center gap-1 cursor-pointer"
                                                >
                                                    <X size={14} />
                                                    Remove file
                                                </button>
                                                {bulkFormatChecking && (
                                                    <p className="text-xs text-gray-500 flex items-center gap-1">
                                                        <span className="border-2 border-gray-400 border-t-transparent rounded-full w-3 h-3 animate-spin"></span>
                                                        Checking file format…
                                                    </p>
                                                )}
                                                {!bulkFormatChecking && bulkFormatValid && !bulkFileError && (
                                                    <p className="text-xs text-green-600 flex items-center gap-1">
                                                        <CheckCircle size={12} />
                                                        File format looks good
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            <label htmlFor="countryBulkUploadInput" className="cursor-pointer">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                                        <Upload size={32} className="text-gray-400" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-700">Drag &amp; drop or click to upload</p>
                                                        <p className="text-xs text-gray-500 mt-1">Accepted formats: .xlsx, .xls (Max 5MB)</p>
                                                    </div>
                                                </div>
                                            </label>
                                        )}
                                        {bulkFileError && (
                                            <p className="mt-3 text-sm text-red-500 flex items-center justify-center gap-1">
                                                <AlertCircle size={14} />
                                                {bulkFileError}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {bulkUploadResult && (
                                    <div className="space-y-4">
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h3 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                                                <CheckCircle size={16} />
                                                Upload Completed
                                            </h3>
                                            <div className="grid grid-cols-3 gap-3">
                                                <div className="bg-white rounded-lg p-3 text-center border border-green-100">
                                                    <p className="text-2xl font-bold text-green-600">{bulkUploadResult.summary?.totalCreated || 0}</p>
                                                    <p className="text-xs text-gray-600 mt-1">Created</p>
                                                </div>
                                                <div className="bg-white rounded-lg p-3 text-center border border-yellow-100">
                                                    <p className="text-2xl font-bold text-yellow-600">{bulkUploadResult.summary?.totalDuplicates || 0}</p>
                                                    <p className="text-xs text-gray-600 mt-1">Duplicates</p>
                                                </div>
                                                <div className="bg-white rounded-lg p-3 text-center border border-red-100">
                                                    <p className="text-2xl font-bold text-red-600">{bulkUploadResult.summary?.totalErrors || 0}</p>
                                                    <p className="text-xs text-gray-600 mt-1">Errors</p>
                                                </div>
                                            </div>
                                            <p className="text-xs text-green-700 mt-3">
                                                Next step: open each newly created country from the list and upload its flag image manually.
                                            </p>
                                        </div>

                                        {bulkUploadResult.created?.length > 0 && (
                                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="bg-green-50 px-4 py-2 border-b border-gray-200">
                                                    <p className="text-sm font-semibold text-green-800">Created Countries ({bulkUploadResult.created.length})</p>
                                                </div>
                                                <div className="max-h-40 overflow-y-auto">
                                                    {bulkUploadResult.created.map((item, idx) => (
                                                        <div key={idx} className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100 last:border-0 flex items-center gap-2">
                                                            <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                                                            <span className="font-medium">{item.countryName}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {bulkUploadResult.duplicates?.length > 0 && (
                                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="bg-yellow-50 px-4 py-2 border-b border-gray-200">
                                                    <p className="text-sm font-semibold text-yellow-800">Skipped Duplicates ({bulkUploadResult.duplicates.length})</p>
                                                </div>
                                                <div className="max-h-40 overflow-y-auto">
                                                    {bulkUploadResult.duplicates.map((item, idx) => (
                                                        <div key={idx} className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100 last:border-0 flex items-center gap-2">
                                                            <AlertCircle size={14} className="text-yellow-500 flex-shrink-0" />
                                                            <span>Row {item.row}:</span>
                                                            <span className="font-medium">{item.countryName}</span>
                                                            <span className="text-xs text-gray-400">(Existing)</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {bulkUploadResult.errors?.length > 0 && (
                                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="bg-red-50 px-4 py-2 border-b border-gray-200">
                                                    <p className="text-sm font-semibold text-red-800">Errors ({bulkUploadResult.errors.length})</p>
                                                </div>
                                                <div className="max-h-40 overflow-y-auto">
                                                    {bulkUploadResult.errors.map((item, idx) => (
                                                        <div key={idx} className="px-4 py-2 text-sm text-red-700 border-b border-gray-100 last:border-0 flex items-center gap-2">
                                                            <X size={14} className="text-red-500 flex-shrink-0" />
                                                            <span>{item.row ? `Row ${item.row}: ` : ''}{item.error || 'Unknown error'}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                                <button
                                    onClick={handleCloseBulkUpload}
                                    disabled={countryBulkUploadLoading}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {bulkUploadResult ? 'Close' : 'Cancel'}
                                </button>
                                {!bulkUploadResult && (
                                    <button
                                        onClick={handleBulkUploadSubmit}
                                        disabled={countryBulkUploadLoading || !bulkSelectedFile || bulkFormatChecking || !bulkFormatValid}
                                        className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        data-testid="country-master-bulk-upload-submit-button"
                                    >
                                        {countryBulkUploadLoading && (
                                            <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                        )}
                                        {countryBulkUploadLoading ? 'Uploading…' : 'Upload File'}
                                    </button>
                                )}
                                {bulkUploadResult && (
                                    <button
                                        onClick={() => {
                                            setBulkSelectedFile(null);
                                            setBulkUploadResult(null);
                                            setBulkFileError('');
                                            if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
                                        }}
                                        className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2"
                                    >
                                        <Upload size={16} />
                                        <span>Upload Another File</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Country;
